import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import {
    FormSelect,
    FormTextarea,
    useServerErrorMessage,
} from '@/components/organisms/FishGroupForms';
import {
    type DiscardFishGroupRequest,
    DiscardReason,
    useDiscardFishGroup,
} from '@/domain/fishGroups';

import styles from './actionModals.module.scss';

interface Props {
    clientId: number;
    fishGroupId: number;
    fishGroupName: string;
    currentLocation?: { cageName: string; locationName: string; fromDate: string } | null;
    isOpen: boolean;
    onClose: () => void;
}

interface FormValues {
    reason: DiscardReason | '';
    notes: string;
}

const DEFAULT_VALUES: FormValues = {
    reason: '',
    notes: '',
};

const REASONS: DiscardReason[] = [
    DiscardReason.operator_error,
    DiscardReason.wrong_cage,
    DiscardReason.duplicate,
    DiscardReason.other,
];

export default function DiscardFishGroupModal(props: Props) {
    const { clientId, fishGroupId, fishGroupName, currentLocation, isOpen, onClose } = props;
    const { t } = useTranslation('fishGroups');
    const idPrefix = useId();
    const formId = `${idPrefix}-form`;
    const {
        handleSubmit,
        control,
        reset,
        formState: { isSubmitting },
    } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

    const handleClose = () => {
        if (isSubmitting) return;
        reset(DEFAULT_VALUES);
        onClose();
    };

    const {
        discard,
        error: serverError,
        reset: resetMutation,
    } = useDiscardFishGroup(clientId, fishGroupId, {
        onSuccess: () => {
            ToastSuccess(t('actions.discard.successToast'), 'fish-group-discard-success');
            reset(DEFAULT_VALUES);
            resetMutation();
            onClose();
        },
    });

    const serverErrorMessage = useServerErrorMessage(serverError);

    const onSubmit = async (values: FormValues) => {
        if (!values.reason) return;
        const requestBody: DiscardFishGroupRequest = {
            reason: values.reason,
            ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        };
        await discard(requestBody).catch(() => {});
    };

    const reasonOptions = REASONS.map((reason) => ({
        value: reason,
        label: t(`actions.discard.reasons.${reason}`),
    }));

    const body = (
        <>
            <p className={styles.contextLine}>
                {t('actions.discard.context')}{' '}
                <span className={styles.contextStrong}>{fishGroupName}</span>
                {currentLocation && (
                    <span className={styles.contextMeta}>
                        {' · '}
                        {currentLocation.cageName} ({currentLocation.locationName})
                    </span>
                )}
            </p>
            <p className={styles.contextLine} style={{ marginTop: 'var(--space-8)' }}>
                {t('actions.discard.warning')}
            </p>
            <form className={styles.form} onSubmit={handleSubmit(onSubmit)} id={formId}>
                <FormSelect
                    control={control}
                    name="reason"
                    label={t('actions.discard.fields.reason')}
                    placeholder={t('actions.discard.fields.reasonPlaceholder')}
                    options={reasonOptions}
                    rules={{ required: true }}
                />
                <FormTextarea
                    control={control}
                    name="notes"
                    label={t('actions.fields.notes')}
                    optional
                />

                {serverErrorMessage && (
                    <Alert tone="danger" size="small">
                        {serverErrorMessage}
                    </Alert>
                )}
            </form>
        </>
    );

    const footer = (
        <>
            {isSubmitting && <Spinner />}
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                {t('actions.cancel')}
            </Button>
            <Button variant="danger" type="submit" form={formId} disabled={isSubmitting}>
                {t('actions.discard.submit')}
            </Button>
        </>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={handleClose}
            title={t('actions.discard.title')}
            body={body}
            footer={footer}
            backdrop="static"
        />
    );
}
